import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, Mail, Phone, ArrowLeft, ExternalLink, CheckCircle2, XCircle, Clock, Shuffle } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import LoaderProgress from '../components/ui/loader-progress';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

function PercentageDonut({ value, color }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex'
      }}
    >
      <CircularProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, value))}
        size={56}
        thickness={5}
        sx={{
          color,
          backgroundColor: 'rgba(145, 158, 171, 0.12)',
          borderRadius: '50%'
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant="subtitle2"
          component="div"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const STATUS_COLORS = {
  created: '#0ea5e9',
  opened: '#3b82f6',
  transition: '#f59e0b',
  terminated: '#ef4444',
  failed: '#b91c1c',
  closed: '#16a34a',
  default: '#6b7280'
};

const STATUS_ICONS = {
  created: Clock,
  opened: Clock,
  transition: Shuffle,
  terminated: XCircle,
  failed: XCircle,
  closed: CheckCircle2,
  default: Clock
};

function getStatusVisuals(status) {
  const key = (status || '').toLowerCase();
  const color = STATUS_COLORS[key] || STATUS_COLORS.default;
  const IconComponent = STATUS_ICONS[key] || STATUS_ICONS.default;
  const label =
    key === 'created'
      ? 'Creata'
      : key === 'opened'
      ? 'Aperta'
      : key === 'transition'
      ? 'In transizione'
      : key === 'terminated'
      ? 'Terminata'
      : key === 'failed'
      ? 'Fallita'
      : key === 'closed'
      ? 'Chiusa'
      : status || 'Senza stato';

  return { color, IconComponent, label };
}

export default function IssuerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const issuerName = location.state?.name || '';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [urgencyOptions, setUrgencyOptions] = useState([]);
  const [statusStats, setStatusStats] = useState([]);
  const [loadingStatusStats, setLoadingStatusStats] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order
      };

      if (search) {
        params.search = search;
      }
      if (filterCategory) {
        params.category = filterCategory;
      }
      if (filterStatus) {
        params.status = filterStatus;
      }
      if (filterUrgency) {
        params.urgency = filterUrgency;
      }

      const res = await api.get(`/negotiation/issuer/${id}`, { params });
      setList(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error('Error fetching negotiations by issuer:', error);
      toast.error('Errore nel caricamento pratiche del creditore');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const buildStatusStatsParams = () => {
    const params = {};
    if (search) {
      params.search = search;
    }
    if (filterCategory) {
      params.category = filterCategory;
    }
    if (filterUrgency) {
      params.urgency = filterUrgency;
    }
    if (issuerName) {
      params.issuer = issuerName;
    }
    return params;
  };

  const fetchStatusStats = async () => {
    setLoadingStatusStats(true);
    try {
      const params = buildStatusStatsParams();
      const res = await api.get('/negotiation/stats/status', { params });
      setStatusStats((res.data && res.data.items) || []);
    } catch (error) {
      console.error('Error fetching negotiation status stats for issuer:', error);
    } finally {
      setLoadingStatusStats(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, page, rowsPerPage, order, orderBy, search, filterCategory, filterStatus, filterUrgency]);

  useEffect(() => {
    fetchStatusStats();
  }, [id, issuerName, search, filterCategory, filterUrgency]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [catRes, filtersRes] = await Promise.all([
          api.get('/category-negotiation'),
          api.get('/negotiation/filters')
        ]);

        setCategoryOptions(
          (catRes.data || [])
            .map((item) => item.name)
            .filter(Boolean)
        );

        setStatusOptions(
          ((filtersRes.data && filtersRes.data.statuses) || []).filter(Boolean)
        );
        setUrgencyOptions(
          ((filtersRes.data && filtersRes.data.urgencies) || []).filter(Boolean)
        );
      } catch (error) {
        console.error('Error fetching issuer detail filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate('/issuer')}
          sx={{ textTransform: 'none' }}
        >
          Indietro
        </Button>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Pratiche – Creditore {issuerName ? `"${issuerName}"` : `#${id}`}
        </Typography>
      </Box>

      {loadingStatusStats && statusStats.length === 0 && (
        <Box sx={{ mb: 3 }}>
          <LoaderProgress size={50} />
        </Box>
      )}

      {!loadingStatusStats && statusStats.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 2
          }}
        >
          {statusStats.map((stat) => {
            const visuals = getStatusVisuals(stat.status);
            const IconComponent = visuals.IconComponent;
            return (
              <Card
                key={stat.status || 'no-status'}
                sx={{
                  borderRadius: 3,
                  boxShadow:
                    '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.16)',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border:
                    filterStatus === stat.status
                      ? `2px solid ${visuals.color}`
                      : '1px solid rgba(145, 158, 171, 0.24)',
                  '&:hover': {
                    boxShadow:
                      '0 0 2px 0 rgba(145, 158, 171, 0.32), 0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => {
                  setFilterStatus(stat.status || '');
                  setPage(0);
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: `${visuals.color}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent size={18} color={visuals.color} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {visuals.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.percentage.toFixed(1)}% delle pratiche del creditore
                  </Typography>
                </Box>
                <PercentageDonut value={stat.percentage} color={visuals.color} />
              </Card>
            );
          })}
        </Box>
      )}

      <StyledCard>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' }
          }}
        >
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={handleSearchChange}
            placeholder="Cerca per riferimento, cliente, categoria..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: { xs: '100%', md: 420 } }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <Autocomplete
              options={categoryOptions}
              value={filterCategory || null}
              onChange={(event, newValue) => {
                setFilterCategory(newValue || '');
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria"
                  size="small"
                />
              )}
              fullWidth
              sx={{ minWidth: { md: 200 } }}
            />

            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: { md: 160 } }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(event) => {
                  setFilterStatus(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{ minWidth: { md: 160 } }}
            >
              <InputLabel>Urgency</InputLabel>
              <Select
                value={filterUrgency}
                label="Urgency"
                onChange={(event) => {
                  setFilterUrgency(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>Tutti</em>
                </MenuItem>
                {urgencyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'reference'}
                    direction={orderBy === 'reference' ? order : 'asc'}
                    onClick={() => handleRequestSort('reference')}
                  >
                    Riferimento
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'customer_name'}
                    direction={orderBy === 'customer_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('customer_name')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'category_name'}
                    direction={orderBy === 'category_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('category_name')}
                  >
                    Categoria
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'urgency'}
                    direction={orderBy === 'urgency' ? order : 'asc'}
                    onClick={() => handleRequestSort('urgency')}
                  >
                    Urgency
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Creato il
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    Nessuna pratica trovata
                  </TableCell>
                </TableRow>
              ) : (
                list.map((n) => (
                  <TableRow key={n.id} hover>
                    <TableCell>{n.id}</TableCell>
                    <TableCell>{n.reference || '-'}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {n.customer_ragione_sociale ||
                           [n.customer_name, n.customer_surname].filter(Boolean).join(' ') ||
                           '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                          {n.customer_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                              <Mail size={12} />
                              {n.customer_email}
                            </Box>
                          )}
                          {n.customer_telephone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                              <Phone size={12} />
                              {n.customer_telephone}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{n.category_name || '-'}</TableCell>
                    <TableCell>{n.status || '-'}</TableCell>
                    <TableCell>{n.urgency || '-'}</TableCell>
                    <TableCell>{n.created_at ? new Date(n.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Apri pratica">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/negotiation/${n.id}`)}
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
